import {
  createContext, useCallback, useMemo, useState,
} from 'react';
import Template from '../models/Template/Template';
import templateAPI from '../services/template';
import { PaginationMetaLink, PaginatorProps } from '../models/Paginator';

interface TemplatesContextProps {
  templateList?: Template[],
  getAllTemplates: (page?: number, perPage?: number, searchText?: string) => Promise<PaginatorProps>,
  getTemplatesFromCategory: (id: string, page?: number, perPage?: number, searchText?: string) => Promise<PaginatorProps>
  submitTemplate: (template: Template, cb?: () => void) => void,
  destroyTemplate: (id: string, cb?: () => void) => void
  loading: boolean,
  errorMessage: string,
}

export const TemplatesContext = createContext<TemplatesContextProps>({} as TemplatesContextProps);
TemplatesContext.displayName = 'TemplatesContext';

interface Props {
  children: JSX.Element
}

export const TemplatesProvider = ({ children }: Props) => {
  const [templateList, setTemplateList] = useState<Template[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const getAllTemplates = useCallback(async (page?:number, perPage?: number, searchText?: string): Promise<{
    currentPage: number,
    links: PaginationMetaLink[],
    total: number,
    to: number,
    from: number,
  }> => {
    setLoading(true);
    const result = await templateAPI.getAllTemplates(page, perPage, 'system-block', searchText);
    setLoading(false);
    setTemplateList(result.data);
    return {
      links: result.meta.links,
      currentPage: result.meta.current_page,
      total: result.meta.total,
      to: result.meta.to || 0,
      from: result.meta.from || 0,
    };
  }, []);

  const getTemplatesFromCategory = useCallback(async (categoryId:string, page?: number, perPage?: number, searchText?: string): Promise<{
    currentPage: number,
    links: PaginationMetaLink[],
    total: number,
    to: number,
    from: number,
  }> => {
    const templatesResult = (await templateAPI.getTemplates(categoryId, page, perPage, 'system-block', searchText));
    setTemplateList(templatesResult.data);
    return {
      currentPage: templatesResult.currentPage,
      links: templatesResult.links,
      total: templatesResult.total,
      to: templatesResult.to || 0,
      from: templatesResult.from || 0,
    };
  }, []);

  const submitTemplate = useCallback((data:Template, cb?: () => void) => {
    if (data && data.id) {
      templateAPI.put(data).then(() => {
        console.log('update template successfully');
        // fetch data again
        if (cb == null) {
          getTemplatesFromCategory(data.categoryId);
        } else {
          cb();
        }
      }).catch(() => {
        console.log('update template int failure');
      });
    } else {
      templateAPI.post(data).then(() => {
        if (cb == null) {
          // fetch data again
          getTemplatesFromCategory(data.categoryId);
        } else {
          cb();
        }
      }).catch((err) => {
        console.log('update template in failure');
        setErrorMessage(err.message);
        console.error(err);
      });
    }
  }, [getTemplatesFromCategory]);

  const destroyTemplate = useCallback((id: string, cb?: () => void) => {
    templateAPI.destroy(id).then(() => {
      // fetch data again
      // 既存のブロックリストから削除します。
      setTemplateList((list) => list.filter((l) => l.id !== id));
      cb();
    }).catch((err) => {
      console.error(err);
    });
  }, []);

  const value = useMemo(() => ({
    templateList,
    loading,
    errorMessage,
    getAllTemplates,
    getTemplatesFromCategory,
    destroyTemplate,
    submitTemplate,
  }), [errorMessage, getAllTemplates, getTemplatesFromCategory, destroyTemplate, loading, templateList, submitTemplate]);

  return (
    <TemplatesContext.Provider value={value}>
      {children}
    </TemplatesContext.Provider>
  );
};
